import { useSelector } from 'react-redux';
import type { BreadcrumbLink } from '@noths/polaris-design-system-candidate-components';

import type { CustomersPhotosProps } from 'src/components/organisms/CustomersPhotos/components/CustomersPhotos';
import type { CustomersReviewsPreviewProps } from 'src/components/organisms/CustomersReviewsPreview/components/CustomersReviewsPreview';
import { selectPageLiveRegionText } from 'src/redux/accessibility/selectors';
import { selectHasRequiredOptions, selectOptions } from 'src/redux/basket/selectors';
import { selectBreadcrumbsLinks } from 'src/redux/breadcrumbs/selectors';
import { selectDeliveryZones } from 'src/redux/delivery/selectors';
import { selectProduct } from 'src/redux/product/selectors';
import { useCustomersPhotosViewModel } from './useCustomersPhotosViewModel';
import { useCustomersReviewsPreviewViewModel } from './useCustomersReviewsPreviewViewModel';
import type { LinkCarouselViewModel } from './useLinkCarouselViewModel';
import { useLinkCarouselViewModel } from './useLinkCarouselViewModel';
import type { MembershipBannerViewModel } from './useMembershipBannerViewModel';
import { useMembershipBannerViewModel } from './useMembershipBannerViewModel';
import type { PartnerViewModel } from './usePartnerViewModel';
import { usePartnerViewModel } from './usePartnerViewModel';
import type { PlpPreviewViewModel } from './usePlpPreviewViewModel';
import { usePlpPreviewViewModel } from './usePlpPreviewViewModel';
import type { ProductFeaturesViewModel } from './useProductFeaturesViewModel';
import { useProductFeaturesViewModel } from './useProductFeaturesViewModel';
import type { ProductInformationViewModel } from './useProductInformationViewModel';
import { useProductInformationViewModel } from './useProductInformationViewModel';
import type { ProductMediaModalViewModel } from './useProductMediaModalViewModel';
import { useProductMediaModalViewModel } from './useProductMediaModalViewModel';
import type { ProductMediaViewModel } from './useProductMediaViewModel';
import { useProductMediaViewModel } from './useProductMediaViewModel';
import type { ProductSummaryViewModel } from './useProductSummaryViewModel';
import { useProductSummaryViewModel } from './useProductSummaryViewModel';
import type { PublicAttributesViewModel } from './usePublicAttributesViewModel';
import { usePublicAttributesViewModel } from './usePublicAttributesViewModel';
import type { RecommendedProductsViewModel } from './useRecommendedProductsViewModel';
import { useRecommendedProductsViewModel } from './useRecommendedProductsViewModel';
import type { ReviewsSummaryViewModel } from './useReviewsSummaryViewModel';
import { useReviewsSummaryViewModel } from './useReviewsSummaryViewModel';
import type { TrustBannerViewModel } from './useTrustBannerViewModel';
import { useTrustBannerViewModel } from './useTrustBannerViewModel';

/**
 * Everything the PDP needs, one entry per part, named for the component it feeds.
 * There is no catch-all bucket, so a new part means a new entry here and nothing
 * else has to change.
 *
 * A part gets its own hook when it has work to do: mapping, tracking, or reading
 * several selectors. A plain read of one piece of state stays a selector and is
 * called here directly, because wrapping it in a hook would add a file and a name
 * without adding behaviour.
 */
export interface PDPLayoutData {
  breadcrumbs: BreadcrumbLink[];
  /** null = strip omitted (loading, failed, or no photos) */
  customersPhotos: CustomersPhotosProps | null;
  customersReviewsPreview: CustomersReviewsPreviewProps;
  deliveryOptionsLoaded: boolean;
  hasProductOptions: boolean;
  /** Whether anything must be configured before buying, which decides where the CTA extras sit */
  hasRequiredOptions: boolean;
  /** null = no related categories, so the section is omitted */
  linkCarousel: LinkCarouselViewModel | null;
  media: ProductMediaViewModel;
  mediaModal: ProductMediaModalViewModel;
  /** null = no banner content, so the section is omitted */
  membershipBanner: MembershipBannerViewModel | null;
  pageLiveRegionText: string;
  partner: PartnerViewModel;
  /** null = no taxonomy node to preview, so the section is omitted */
  plpPreview: PlpPreviewViewModel | null;
  productFeatures: ProductFeaturesViewModel;
  productInformation: ProductInformationViewModel;
  productSummary: ProductSummaryViewModel;
  publicAttributes: PublicAttributesViewModel;
  recommendedProducts: RecommendedProductsViewModel;
  /** null = no AI summary, so the section is omitted */
  reviewsSummary: ReviewsSummaryViewModel | null;
  similarProductsServingConfig: 'pdp_similar_products' | 'pdp-similar-products-bury-sale';
  /** null = no banner content, so the section is omitted */
  trustBanner: TrustBannerViewModel | null;
}

export const usePDPViewModel = (): PDPLayoutData => {
  const product = useSelector(selectProduct);
  const options = useSelector(selectOptions);
  const hasRequiredOptions = useSelector(selectHasRequiredOptions);
  const deliveryZones = useSelector(selectDeliveryZones);
  const breadcrumbs = useSelector(selectBreadcrumbsLinks);
  const linkCarousel = useLinkCarouselViewModel();
  const media = useProductMediaViewModel();
  const mediaModal = useProductMediaModalViewModel();
  const customersPhotos = useCustomersPhotosViewModel();
  const customersReviewsPreview = useCustomersReviewsPreviewViewModel();
  const membershipBanner = useMembershipBannerViewModel();
  const pageLiveRegionText = useSelector(selectPageLiveRegionText);
  const partner = usePartnerViewModel();
  const plpPreview = usePlpPreviewViewModel();
  const productFeatures = useProductFeaturesViewModel();
  const productInformation = useProductInformationViewModel();
  const productSummary = useProductSummaryViewModel();
  const publicAttributes = usePublicAttributesViewModel();
  const recommendedProducts = useRecommendedProductsViewModel();
  const reviewsSummary = useReviewsSummaryViewModel();
  const trustBanner = useTrustBannerViewModel();

  return {
    breadcrumbs,
    customersPhotos,
    customersReviewsPreview,
    deliveryOptionsLoaded: Boolean(deliveryZones),
    hasProductOptions: options.length > 0,
    hasRequiredOptions,
    linkCarousel,
    media,
    mediaModal,
    membershipBanner,
    pageLiveRegionText,
    partner,
    plpPreview,
    productFeatures,
    productInformation,
    productSummary,
    publicAttributes,
    recommendedProducts,
    reviewsSummary,
    similarProductsServingConfig: product.onSale
      ? 'pdp_similar_products'
      : 'pdp-similar-products-bury-sale',
    trustBanner,
  };
};
